/**
 * CenoX Platform Admin: every business, subscription and payment, AI
 * economics, CenoX's own P&L, the platform audit trail and who holds admin.
 *
 * Only reachable for a user the server says holds `platform_admin`; the API
 * re-checks on every request regardless, and each read is one signed
 * SECURITY DEFINER function (0131, 0137). Being the owner or a member of
 * the CenoX workspace grants nothing: the sidebar offers this page inside the
 * company workspace, but only to a platform admin.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { CenoxShell } from '../../layouts/CenoxShell';
import { Badge, Button, Card, CardHeader, Input, Select, Spinner, cn } from '../../components/ui';
import { money } from '../../lib/format';

// ── Who may see it ─────────────────────────────────────────────────────────

interface PlatformMe {
  platformAdmin: boolean;
  companyBusinessId: string | null;
}

export function usePlatformMe(): PlatformMe {
  const q = useQuery({
    queryKey: ['platform-admin-me'],
    queryFn: () => api.get<PlatformMe>('/platform-admin/me'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { platformAdmin: q.data?.platformAdmin === true, companyBusinessId: q.data?.companyBusinessId ?? null };
}

export function usePlatformAdmin(): boolean {
  return usePlatformMe().platformAdmin;
}

// ── Shared pieces ──────────────────────────────────────────────────────────

const TABS = [
  ['revenue', 'Revenue'],
  ['businesses', 'Businesses'],
  ['subscriptions', 'Subscriptions'],
  ['payments', 'Payments'],
  ['ai', 'AI Economics'],
  ['audit', 'Audit'],
  ['admins', 'Platform Administrators'],
] as const;
type Tab = (typeof TABS)[number][0];

interface QueryLike<T> {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data?: T | undefined;
}

function Loading<T>({ q, children }: { q: QueryLike<T>; children: (d: T) => ReactNode }): ReactNode {
  if (q.isPending) return <Spinner />;
  if (q.isError || q.data === undefined) {
    return <Card><p className="text-sm text-slate-700">{q.error instanceof ApiError ? q.error.message : 'Could not load.'}</p></Card>;
  }
  return children(q.data);
}

function Grid({ head, rows, empty = 'Nothing to show.' }: { head: string[]; rows: ReactNode[][]; empty?: string }): ReactNode {
  if (rows.length === 0) return <p className="text-sm text-slate-500">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs text-slate-500">
          <tr>{head.map((h, i) => <th key={i} className="whitespace-nowrap py-1.5 pr-4 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j} className="whitespace-nowrap py-1.5 pr-4 text-slate-700">{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const day = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(0, 10) : '—');
const neg = (v: string) => (Number(v) < 0 ? 'text-red-700' : '');

/** "This month" is resolved on the server, in CenoX's timezone; the rest are sent as days. */
type Period = { preset: 'month' | 'last' | 'quarter' | 'year' | 'custom'; from: string; to: string };

function periodQuery(p: Period): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  switch (p.preset) {
    case 'month':
      return '';
    case 'last':
      return `?from=${iso(new Date(y, m - 1, 1))}&to=${iso(new Date(y, m, 0))}`;
    case 'quarter':
      return `?from=${iso(new Date(y, m - (m % 3), 1))}&to=${iso(now)}`;
    case 'year':
      return `?from=${y}-01-01&to=${iso(now)}`;
    default:
      return p.from && p.to ? `?from=${p.from}&to=${p.to}` : '';
  }
}

function PeriodPicker({ value, onChange }: { value: Period; onChange: (p: Period) => void }): ReactNode {
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Select
        label="Accounting period"
        value={value.preset}
        onChange={(e) => onChange({ ...value, preset: e.target.value as Period['preset'] })}
        options={[
          { value: 'month', label: 'This month to date' },
          { value: 'last', label: 'Last month' },
          { value: 'quarter', label: 'This quarter to date' },
          { value: 'year', label: 'This year to date' },
          { value: 'custom', label: 'Custom…' },
        ]}
      />
      {value.preset === 'custom' && (
        <>
          <Input label="From" type="date" value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })} />
          <Input label="To (inclusive)" type="date" value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} />
        </>
      )}
    </div>
  );
}

// ── Revenue and the company P&L ────────────────────────────────────────────

interface Revenue {
  from: string;
  to: string;
  timezone: string;
  companyWorkspace: string | null;
  subscribers: { payingSubscribers: number; trialing: number; internal: number };
  subscriptionRevenue: Array<{ currency: string; amount: string; invoices: number }>;
  ai: Array<{
    currency: string; requests: number; tokens: number; providerCostUsd: string; providerCost: string;
    unconvertedCostUsd: string; includedConsumedPaying: string; includedConsumedInternal: string;
    promotionalConsumed: string; paidCreditConsumed: string; aiRevenue: string; grossProfit: string;
  }>;
  credit: {
    paidTopUpsAvailable: boolean;
    topUpCashReceived: Array<{ currency: string; amount: string }>;
    unusedPaidCreditLiability: Array<{ currency: string; amount: string }>;
    promotionalBalanceOutstanding: Array<{ currency: string; amount: string }>;
  };
  operatingExpenses: Array<{ currency: string; category: string; amount: string; count: number }>;
  profit: Array<{ currency: string; subscriptionRevenue: string; aiRevenue: string; aiProviderCost: string; operatingExpenses: string; profit: string }>;
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }): ReactNode {
  return (
    <Card>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={cn('mt-1 whitespace-nowrap text-lg font-semibold text-slate-900', tone)}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </Card>
  );
}

function RevenueTab({ period }: { period: Period }): ReactNode {
  const qs = periodQuery(period);
  const q = useQuery({ queryKey: ['platform-admin-revenue', qs], queryFn: () => api.get<Revenue>(`/platform-admin/revenue${qs}`), retry: false });
  return (
    <Loading q={q}>
      {(r) => {
        const main = r.profit[0];
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-900">{r.from} to {r.to}</span>, whole days in{' '}
              <span className="font-medium text-slate-900">{r.timezone}</span>
              {r.companyWorkspace ? ' (the CenoX company workspace’s timezone).' : ' — no company workspace named yet.'}
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Paying subscribers" value={String(r.subscribers.payingSubscribers)} hint={`${r.subscribers.trialing} trialing · ${r.subscribers.internal} internal (not counted)`} />
              <Stat label="Subscription revenue" value={main ? money(main.subscriptionRevenue, main.currency) : '—'} hint="Paid invoices, internal businesses excluded" />
              <Stat label="AI provider cost" value={main ? money(main.aiProviderCost, main.currency) : '—'} hint="Every CenoX-managed request" />
              <Stat label="Company profit" value={main ? money(main.profit, main.currency) : '—'} tone={main ? neg(main.profit) : ''} hint="After operating expenses" />
            </div>

            <Card>
              <CardHeader title="Company P&L" subtitle="Per currency — currencies are never added together." />
              <Grid
                head={['Currency', 'Subscription revenue', 'AI revenue', 'AI provider cost', 'Operating expenses', 'Profit']}
                rows={r.profit.map((p) => [
                  p.currency, money(p.subscriptionRevenue, p.currency), money(p.aiRevenue, p.currency),
                  `− ${money(p.aiProviderCost, p.currency)}`, `− ${money(p.operatingExpenses, p.currency)}`,
                  <span className={cn('font-semibold', neg(p.profit))}>{money(p.profit, p.currency)}</span>,
                ])}
                empty="No revenue, AI cost or expenses in this period."
              />
            </Card>

            <Card>
              <CardHeader title="AI revenue and cost" subtitle="Revenue is paid credit consumed. Plan allowances are earned as subscription revenue; promotional credit is never revenue." />
              <Grid
                head={['Currency', 'Requests', 'Cost (USD)', 'Cost', 'Revenue (paid credit)', 'Plan allowance', 'Internal', 'Promotional', 'Gross profit']}
                rows={r.ai.map((a) => [
                  a.currency, a.requests.toLocaleString(), `$${Number(a.providerCostUsd).toFixed(4)}`,
                  money(a.providerCost, a.currency), money(a.aiRevenue, a.currency),
                  money(a.includedConsumedPaying, a.currency), money(a.includedConsumedInternal, a.currency),
                  money(a.promotionalConsumed, a.currency),
                  <span className={neg(a.grossProfit)}>{money(a.grossProfit, a.currency)}</span>,
                ])}
                empty="No CenoX-managed AI in this period."
              />
              {r.ai.some((a) => Number(a.unconvertedCostUsd) > 0) && (
                <p className="mt-2 text-xs text-amber-700">Some provider cost had no stored exchange rate and is shown in USD only, not in the P&L.</p>
              )}
            </Card>

            <div className="grid gap-3 lg:grid-cols-3">
              <Card>
                <CardHeader title="AI top-up cash received" />
                <p className="text-sm text-slate-600">{r.credit.paidTopUpsAvailable ? '' : 'None: CenoX does not sell AI credit yet.'}</p>
              </Card>
              <Card>
                <CardHeader title="Unused paid credit" subtitle="A liability until consumed." />
                <p className="text-sm text-slate-600">{r.credit.paidTopUpsAvailable ? '' : 'None: no paid credit has been sold.'}</p>
              </Card>
              <Card>
                <CardHeader title="Promotional credit outstanding" subtitle="Free credit. Not revenue, not a liability." />
                <Grid head={['Currency', 'Balance']} rows={r.credit.promotionalBalanceOutstanding.map((p) => [p.currency, money(p.amount, p.currency)])} empty="None." />
              </Card>
            </div>

            <Card>
              <CardHeader title="Operating expenses" subtitle="Recorded in the CenoX company workspace only — never CenoHub's or any other business's." />
              {!r.companyWorkspace ? (
                <p className="text-sm text-slate-600">Name the company workspace under Platform Administrators to include CenoX's expenses.</p>
              ) : (
                <Grid
                  head={['Category', 'Entries', 'Amount']}
                  rows={r.operatingExpenses.map((e) => [e.category, String(e.count), money(e.amount, e.currency)])}
                  empty="No expenses recorded in this period."
                />
              )}
            </Card>
          </div>
        );
      }}
    </Loading>
  );
}

// ── Businesses ─────────────────────────────────────────────────────────────

interface BusinessRow {
  id: string; name: string; type: string; country: string; currency: string; createdAt: string;
  ownerName: string | null; ownerEmail: string | null; companyWorkspace: boolean; internalUse: boolean; paidEver: boolean;
  subscriptions: Array<{ product: string; planName: string | null; status: string; complimentaryUntil: string | null }>;
}

function classification(b: { companyWorkspace?: boolean; internalUse?: boolean; internal?: boolean }): ReactNode {
  if (b.companyWorkspace) return <Badge tone="info">CenoX company</Badge>;
  if (b.internalUse || b.internal) return <Badge tone="neutral">Internal use</Badge>;
  return null;
}

function BusinessesTab(): ReactNode {
  const q = useQuery({ queryKey: ['platform-admin-overview'], queryFn: () => api.get<{ data: BusinessRow[] }>('/platform-admin/overview/businesses'), retry: false });
  return (
    <Loading q={q}>
      {(d) => (
        <Card>
          <CardHeader title={`${d.data.length} registered businesses`} subtitle="Internal businesses are never counted as paying subscribers." />
          <Grid
            head={['Business', '', 'Owner', 'Country', 'Plans', 'Paid ever', 'Registered']}
            rows={d.data.map((b) => [
              <span className="font-medium text-slate-900">{b.name}</span>,
              classification(b),
              <span title={b.ownerEmail ?? ''}>{b.ownerName ?? '—'}</span>,
              b.country,
              b.subscriptions.map((s) => `${s.planName ?? s.product} · ${s.status}`).join(', ') || '—',
              b.paidEver ? 'Yes' : 'No',
              day(b.createdAt),
            ])}
          />
        </Card>
      )}
    </Loading>
  );
}

// ── Subscriptions ──────────────────────────────────────────────────────────

interface SubscriptionRow {
  businessId: string; businessName: string; internal: boolean; productName: string; planName: string | null;
  status: string; interval: string; periodEnd: string; trialEndsAt: string | null; complimentaryUntil: string | null;
  cancelAtPeriodEnd: boolean; paying: boolean;
  lastInvoice: { number: string; status: string; amount: string; currency: string } | null;
}

function SubscriptionsTab(): ReactNode {
  const q = useQuery({ queryKey: ['platform-admin-subscriptions'], queryFn: () => api.get<{ data: SubscriptionRow[] }>('/platform-admin/subscriptions'), retry: false });
  return (
    <Loading q={q}>
      {(d) => {
        const paying = d.data.filter((s) => s.paying).length;
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader title={`${d.data.length} subscriptions · ${paying} paying`} />
              <Grid
                head={['Business', '', 'Product', 'Plan', 'Status', 'Billing', 'Renews / ends', 'Last invoice']}
                rows={d.data.map((s) => [
                  <span className="font-medium text-slate-900">{s.businessName}</span>,
                  s.paying ? <Badge tone="success">Paying</Badge> : classification(s),
                  s.productName,
                  s.planName ?? '—',
                  s.complimentaryUntil ? `${s.status} (no charge)` : s.status,
                  s.interval,
                  day(s.complimentaryUntil ?? s.trialEndsAt ?? s.periodEnd),
                  s.lastInvoice ? `${s.lastInvoice.number} · ${s.lastInvoice.status} · ${money(s.lastInvoice.amount, s.lastInvoice.currency)}` : '—',
                ])}
              />
            </Card>
            <ManualActivation />
          </div>
        );
      }}
    </Loading>
  );
}

interface ManualActivationResult {
  invoice: { number: string; amount: string; currencyCode: string; status: string };
  voided: string[];
  planKey: string;
  interval: string;
  periodStart: string;
  periodEnd: string;
}

const MANUAL_PLANS = [
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'business', label: 'Business' },
];

/**
 * Start a paid plan before payment, with its price left owing. Not a payment
 * and not a grant: the invoice stays open, older open plan invoices are
 * voided, and the business's activity log names who did it.
 */
function ManualActivation(): ReactNode {
  const queryClient = useQueryClient();
  const businesses = useQuery({
    queryKey: ['platform-admin-businesses'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/platform-admin/businesses'),
  });
  const [businessId, setBusinessId] = useState('');
  const [planKey, setPlanKey] = useState('starter');
  const [interval, setBillingInterval] = useState('yearly');
  const [reason, setReason] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const ready = Boolean(businessId) && reason.trim().length >= 10;
  const businessName = businesses.data?.data.find((b) => b.id === businessId)?.name ?? '';
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setReviewing(false);
  };

  const activate = useMutation({
    mutationFn: () =>
      api.post<ManualActivationResult>(`/platform-admin/billing/businesses/${businessId}/manual-activation`, {
        planKey, interval, reason: reason.trim(),
      }),
    onSuccess: (r) => {
      setMessage(
        `Active from ${day(r.periodStart)} to ${day(r.periodEnd)}. Invoice ${r.invoice.number} ` +
          `(${money(r.invoice.amount, r.invoice.currencyCode)}) is open and unpaid.` +
          (r.voided.length ? ` Voided ${r.voided.join(', ')}.` : ''),
      );
      setReviewing(false);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['platform-admin-subscriptions'] });
    },
    onError: (e) => {
      setReviewing(false);
      setMessage(e instanceof ApiError ? e.message : 'Could not activate the plan.');
    },
  });

  return (
    <Card>
      <CardHeader
        title="Activate a plan manually"
        subtitle="Starts the plan now for one billing period at its published price. The invoice stays open as owed — nothing is marked paid. Older open plan invoices are voided so they cannot be paid over it. Audited in that business's activity log."
      />
      <form
        className="grid gap-3 sm:grid-cols-2"
        noValidate
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!ready) return;
          if (!reviewing) {
            setMessage(null);
            setReviewing(true);
            return;
          }
          activate.mutate();
        }}
      >
        <Select
          label="Business"
          value={businessId}
          onChange={(e) => edit(setBusinessId)(e.target.value)}
          placeholder="Choose a business…"
          options={(businesses.data?.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
        />
        <Select label="Plan" value={planKey} onChange={(e) => edit(setPlanKey)(e.target.value)} options={MANUAL_PLANS} />
        <Select
          label="Billing interval"
          value={interval}
          onChange={(e) => edit(setBillingInterval)(e.target.value)}
          options={[{ value: 'yearly', label: 'Yearly' }, { value: 'monthly', label: 'Monthly' }]}
        />
        <Input label="Reason (at least 10 characters)" value={reason} onChange={(e) => edit(setReason)(e.target.value)} />
        {reviewing && (
          <p role="alert" className="sm:col-span-2 text-sm text-slate-700">
            Activate {MANUAL_PLANS.find((p) => p.value === planKey)?.label} ({interval}) for {businessName} from today,
            with its invoice left unpaid?
          </p>
        )}
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" loading={activate.isPending} disabled={!ready}>
            {reviewing ? 'Confirm activation' : 'Review activation'}
          </Button>
          {reviewing && <Button type="button" variant="secondary" onClick={() => setReviewing(false)}>Cancel</Button>}
          {message && <span role="status" className="text-sm text-slate-600">{message}</span>}
        </div>
      </form>
    </Card>
  );
}

// ── Payments ───────────────────────────────────────────────────────────────

interface Payments {
  from: string; to: string; timezone: string;
  rows: Array<{ kind: string; businessName: string; internal: boolean; invoiceNumber: string; amount: string; currency: string; status: string; provider: string; method: string | null; at: string }>;
}

const PAYMENT_TONE: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = { succeeded: 'success', failed: 'danger', refunded: 'warning', pending: 'neutral' };

function PaymentsTab({ period }: { period: Period }): ReactNode {
  const qs = periodQuery(period);
  const q = useQuery({ queryKey: ['platform-admin-payments', qs], queryFn: () => api.get<Payments>(`/platform-admin/payments${qs}`), retry: false });
  return (
    <Loading q={q}>
      {(d) => (
        <Card>
          <CardHeader title="Subscription payments" subtitle={`${d.from} to ${d.to} (${d.timezone}). Provider attempts of every outcome, and transfers settled by CenoX staff.`} />
          <Grid
            head={['When', 'Business', '', 'Invoice', 'Amount', 'Status', 'Via']}
            rows={d.rows.map((p) => [
              day(p.at),
              <span className="font-medium text-slate-900">{p.businessName}</span>,
              p.internal ? <Badge tone="neutral">Internal</Badge> : null,
              p.invoiceNumber,
              money(p.amount, p.currency),
              <Badge tone={PAYMENT_TONE[p.status] ?? 'neutral'}>{p.status}</Badge>,
              p.kind === 'manual' ? 'Settled by CenoX' : [p.provider, p.method].filter(Boolean).join(' · '),
            ])}
            empty="No payments in this period."
          />
        </Card>
      )}
    </Loading>
  );
}

// ── AI economics (the existing view) and promotional credit ───────────────

interface Group { requests: number; tokens: number; providerCostUsd: string; providerCostInCurrency: string; revenue: string; grossMargin: string }
interface Usage {
  totalsByCurrency: Array<Group & { currency: string }>;
  byBusiness: Array<Group & { businessId: string; businessName: string; currency: string }>;
  byModel: Array<Group & { model: string; currency: string }>;
  bringYourOwnKey: Array<{ businessId: string; businessName: string; requests: number; tokens: number }>;
}

function usageRows<R extends Group & { currency: string }>(rows: R[], first: (r: R) => string): ReactNode[][] {
  return rows.map((r) => [
    <span className="font-medium text-slate-900">{first(r)}</span>, r.currency, String(r.requests), r.tokens.toLocaleString(),
    `$${r.providerCostUsd}`, r.providerCostInCurrency, r.revenue, <span className={neg(r.grossMargin)}>{r.grossMargin}</span>,
  ]);
}

function AiTab(): ReactNode {
  const q = useQuery({ queryKey: ['platform-admin-usage'], queryFn: () => api.get<Usage>('/platform-admin/ai/usage'), retry: false });
  const head = ['', 'Currency', 'Requests', 'Tokens', 'Provider cost (USD)', 'Cost (in currency)', 'Charged', 'Margin'];
  return (
    <Loading q={q}>
      {(u) => (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">This month. “Charged” includes plan allowances and promotional credit; the Revenue tab shows what counts as revenue.</p>
          <Card><CardHeader title="Totals" /><Grid head={head} rows={usageRows(u.totalsByCurrency, () => 'All businesses')} /></Card>
          <Card><CardHeader title="By business" /><Grid head={head} rows={usageRows(u.byBusiness, (r) => r.businessName)} /></Card>
          <Card><CardHeader title="By model" /><Grid head={head} rows={usageRows(u.byModel, (r) => r.model)} /></Card>
          <Card>
            <CardHeader title="Bring your own key" subtitle="The customer's own spend with the provider — not CenoX revenue." />
            <Grid head={['Business', 'Requests', 'Tokens']} rows={u.bringYourOwnKey.map((b) => [b.businessName, String(b.requests), b.tokens.toLocaleString()])} empty="None this month." />
          </Card>
          <GrantCredit />
        </div>
      )}
    </Loading>
  );
}

function GrantCredit(): ReactNode {
  const queryClient = useQueryClient();
  const businesses = useQuery({
    queryKey: ['platform-admin-businesses'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/platform-admin/businesses'),
  });
  const [businessId, setBusinessId] = useState('');
  const [amount, setAmount] = useState('');
  const [currencyCode, setCurrency] = useState('NGN');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const grant = useMutation({
    mutationFn: () =>
      api.post<{ status: string }>(`/platform-admin/businesses/${businessId}/ai-credit`, {
        amount, currencyCode, reason, idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: (r) => {
      setMessage(r.status === 'granted' ? 'Credit granted.' : 'Already applied.');
      setAmount('');
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['platform-admin-usage'] });
    },
    onError: (e) => setMessage(e instanceof ApiError ? e.message : 'Could not grant credit.'),
  });

  return (
    <Card>
      <CardHeader title="Promotional AI credit" subtitle="Free credit added to a business's top-up balance. Never revenue. Audited in that business's activity log." />
      <form
        className="grid gap-3 sm:grid-cols-2"
        noValidate
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (businessId && amount && reason.trim().length >= 3) grant.mutate();
        }}
      >
        <Select
          label="Business"
          value={businessId}
          onChange={(e) => setBusinessId(e.target.value)}
          placeholder="Choose a business…"
          options={(businesses.data?.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
        />
        <Input label="Amount (negative to correct)" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
        <Select
          label="Currency"
          value={currencyCode}
          onChange={(e) => setCurrency(e.target.value)}
          options={[{ value: 'NGN', label: 'NGN' }, { value: 'USD', label: 'USD' }]}
        />
        <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" loading={grant.isPending} disabled={!businessId || !amount || reason.trim().length < 3}>
            Apply credit
          </Button>
          {message && <span role="status" className="text-sm text-slate-600">{message}</span>}
        </div>
      </form>
    </Card>
  );
}

// ── Audit ──────────────────────────────────────────────────────────────────

interface AuditRow { id: string; actorName: string | null; entityType: string; summary: string; before: unknown; after: unknown; createdAt: string }

function AuditTab(): ReactNode {
  const q = useQuery({ queryKey: ['platform-admin-audit'], queryFn: () => api.get<{ data: AuditRow[] }>('/platform-admin/audit?limit=200'), retry: false });
  return (
    <Loading q={q}>
      {(d) => (
        <Card>
          <CardHeader title="Platform audit log" subtitle="Changes CenoX staff made to things no single business owns. A business's own changes are in its activity log." />
          <Grid
            head={['When (UTC)', 'Who', 'What', 'Change']}
            rows={d.data.map((a) => [
              new Date(a.createdAt).toISOString().replace('T', ' ').slice(0, 16),
              a.actorName ?? '—',
              a.summary,
              <code className="text-xs text-slate-500">{JSON.stringify(a.before)} → {JSON.stringify(a.after)}</code>,
            ])}
            empty="No platform changes recorded yet."
          />
        </Card>
      )}
    </Loading>
  );
}

// ── Platform administrators and the company workspace ─────────────────────

interface AdminRow { userId: string; name: string | null; email: string | null; role: string; active: boolean; lastLoginAt: string | null; grantedAt: string; grantedBy: string | null; you: boolean }

function AdminsTab(): ReactNode {
  const queryClient = useQueryClient();
  const admins = useQuery({ queryKey: ['platform-admin-admins'], queryFn: () => api.get<{ data: AdminRow[] }>('/platform-admin/admins'), retry: false });
  const company = useQuery({
    queryKey: ['platform-admin-company'],
    queryFn: () => api.get<{ businessId: string | null; businessName?: string; updatedBy?: string }>('/platform-admin/company-workspace'),
    retry: false,
  });
  const businesses = useQuery({ queryKey: ['platform-admin-businesses'], queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/platform-admin/businesses') });
  const [choice, setChoice] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (businessId: string | null) => api.put('/platform-admin/company-workspace', { businessId }),
    onSuccess: () => {
      setMessage('Saved. Recorded in the platform audit log.');
      void queryClient.invalidateQueries({ queryKey: ['platform-admin-company'] });
      void queryClient.invalidateQueries({ queryKey: ['platform-admin-me'] });
    },
    onError: (e) => setMessage(e instanceof ApiError ? e.message : 'Could not save.'),
  });

  return (
    <div className="space-y-4">
      <Loading q={admins}>
        {(d) => (
          <Card>
            <CardHeader
              title={`${d.data.length} platform administrator${d.data.length === 1 ? '' : 's'}`}
              subtitle="Everyone who can open this workspace. The role is held by a user — never by a business, its name or its members."
            />
            <Grid
              head={['Name', 'Email', 'Role', 'Status', 'Last sign-in', 'Granted']}
              rows={d.data.map((a) => [
                <span className="font-medium text-slate-900">{a.name ?? '—'}{a.you && <span className="ml-1 text-xs text-slate-500">(you)</span>}</span>,
                a.email ?? '—',
                a.role,
                a.active ? <Badge tone="success">Active</Badge> : <Badge tone="danger">Inactive</Badge>,
                day(a.lastLoginAt),
                `${day(a.grantedAt)}${a.grantedBy ? ` by ${a.grantedBy}` : ' (bootstrap)'}`,
              ])}
            />
          </Card>
        )}
      </Loading>
      <Loading q={company}>
        {(c) => (
          <Card>
            <CardHeader
              title="CenoX company workspace"
              subtitle="Where CenoX's own operating expenses are kept, and where Platform Admin appears in the sidebar. Chosen here by id — a business's name never decides it. Treated as internal."
            />
            <p className="mb-3 text-sm text-slate-700">
              Currently: <span className="font-medium">{c.businessId ? c.businessName ?? c.businessId : 'not named'}</span>
              {c.updatedBy && <span className="text-slate-500"> · set by {c.updatedBy}</span>}
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <Select
                label="Company workspace"
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                placeholder="Choose a business…"
                options={(businesses.data?.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
              />
              <Button onClick={() => choice && save.mutate(choice)} disabled={!choice} loading={save.isPending}>Save</Button>
              {c.businessId && <Button variant="secondary" onClick={() => save.mutate(null)}>Clear</Button>}
              {message && <span role="status" className="text-sm text-slate-600">{message}</span>}
            </div>
          </Card>
        )}
      </Loading>
    </div>
  );
}

// ── The page ───────────────────────────────────────────────────────────────

export function PlatformAdminPage(): ReactNode {
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.find(([key]) => key === params.get('tab'))?.[0] ?? 'revenue';
  const [period, setPeriod] = useState<Period>({ preset: 'month', from: '', to: '' });

  return (
    <CenoxShell>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Platform Admin</h1>
            <p className="text-sm text-slate-500">CenoX across every business. Visible only to CenoX platform administrators.</p>
          </div>
          {(tab === 'revenue' || tab === 'payments') && <PeriodPicker value={period} onChange={setPeriod} />}
        </div>
        <nav className="flex gap-1 overflow-x-auto border-b border-slate-200" aria-label="Platform Admin">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setParams({ tab: key }, { replace: true })}
              aria-current={tab === key ? 'page' : undefined}
              className={cn(
                '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
                tab === key ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-800',
              )}
            >
              {label}
            </button>
          ))}
        </nav>
        {tab === 'revenue' && <RevenueTab period={period} />}
        {tab === 'businesses' && <BusinessesTab />}
        {tab === 'subscriptions' && <SubscriptionsTab />}
        {tab === 'payments' && <PaymentsTab period={period} />}
        {tab === 'ai' && <AiTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'admins' && <AdminsTab />}
      </div>
    </CenoxShell>
  );
}
